import React, { useState, useRef, useEffect } from 'react';
import {
    PlayCircle,
    Download,
    CheckCircle2,
    Save,
    History,
    Upload,
} from 'lucide-react';
import ValidationPopup from './ValidationPopup';

import { PrismLight as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import python from 'react-syntax-highlighter/dist/esm/languages/prism/python';

SyntaxHighlighter.registerLanguage('python', python);

type AgentPlaygroundProps = {
    codeFromChat?: string;
    codeLoading?: boolean;
};

const AgentPlayground: React.FC<AgentPlaygroundProps> = ({ codeFromChat, codeLoading }) => {
    const [code, setCode] = useState<string>(codeFromChat || '');

    useEffect(() => {
        if (codeFromChat !== undefined) {
            setCode(codeFromChat);
        }
    }, [codeFromChat]);

    const [showVersionDropdown, setShowVersionDropdown] = useState(false);
    const [versions, setVersions] = useState([
        {
            version: 'v1.0',
            timestamp: '2025-07-03 14:22',
            user: 'System',
            status: 'bot',
            comment: 'Initial version generated by the AI builder.',
        },
        {
            version: 'v1.1',
            timestamp: '2025-07-03 15:02',
            user: 'You',
            status: 'custom deployed',
            comment: 'Tweaked discount logic and retraining step.',
        },
    ]);

    const [showSaveDialog, setShowSaveDialog] = useState(false);
    const [saveComment, setSaveComment] = useState('');

    const handleSave = () => {
        setVersions([
            ...versions,
            {
                version: `v${(versions.length + 1).toFixed(1)}`,
                timestamp: new Date().toLocaleString(),
                user: 'You',
                status: 'custom deployed',
                comment: saveComment || 'No comment provided.',
            },
        ]);
        setSaveComment('');
        setShowSaveDialog(false);
    };

    const [showValidationPopup, setShowValidationPopup] = useState(false);

    const validationData = {
        outputData: [
            { name: 'Alice', age: 29, loanStatus: 'Approved' },
            { name: 'Bob', age: 35, loanStatus: 'Rejected' }
        ],
        timeTaken: 2.3,
        llmUsed: 'GPT-4o',
        errorDetails: null,
        entityInfo: {
            entity: 'CustomerData',
            database: 'FinanceDB'
        }
    };

    return (
        <div className="h-full w-full flex flex-col">
            {/* Toolbar */}
            <div className="flex items-center gap-3 px-3 py-2 border-b border-neutral-800 bg-neutral-950">
                <button className="text-gray-400 hover:text-white" title="Run">
                    <PlayCircle className="w-4 h-4" />
                </button>
                <button className="text-gray-400 hover:text-white" title="Download">
                    <Download className="w-4 h-4" />
                </button>
                <button
                    className="text-gray-400 hover:text-white"
                    title="Validate"
                    onClick={() => setShowValidationPopup(true)}
                >
                    <CheckCircle2 className="w-4 h-4" />
                </button>
                <button className="text-gray-400 hover:text-white" title="Upload">
                    <Upload className="w-4 h-4" />
                </button>
                <button
                    className="text-gray-400 hover:text-white"
                    title="Save"
                    onClick={() => setShowSaveDialog(true)}
                >
                    <Save className="w-4 h-4" />
                </button>

                <div className="relative dropdown-parent">
                    <button
                        onClick={() => setShowVersionDropdown(!showVersionDropdown)}
                        className="text-gray-400 hover:text-white text-xs flex items-center gap-1"
                        title="View saved versions"
                    >
                        <History className="w-4 h-4" />
                        {versions[versions.length - 1]?.version}
                    </button>

                    {showVersionDropdown && (
                        <div className="absolute right-0 mt-2 w-72 bg-neutral-900 border border-neutral-800 rounded-md shadow-md z-50 text-xs max-h-64 overflow-auto">
                            {versions.map((v, i) => (
                                <div
                                    key={i}
                                    className="flex justify-between px-3 py-2 hover:bg-neutral-800 text-gray-300 cursor-pointer"
                                    title={v.comment}
                                >
                                    <div className="font-medium text-indigo-400">{v.version}</div>
                                    <div className="text-gray-400">{v.timestamp}</div>
                                    <div className="text-gray-500">{v.user}</div>
                                </div>
                            ))}
                            {versions.length === 0 && (
                                <div className="text-gray-500 p-3 text-center">No versions yet</div>
                            )}
                        </div>
                    )}
                </div>

                <span className="ml-auto text-xs text-gray-500">Language: Python</span>
            </div>

            {/* Editor */}
            <div className="flex-1 bg-neutral-950 p-4 overflow-auto">
                {!code.trim() ? (
                    <div className="flex flex-col items-center justify-center h-full min-h-[400px] bg-neutral-900 border border-neutral-800 rounded-md text-center text-gray-400 p-6">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-10 h-10 mb-4 text-indigo-500"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth="1.5"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M16.862 4.487c-.44-1.26-1.97-2.362-3.456-2.445-1.489-.084-2.862.931-3.35 2.223a6.937 6.937 0 00-.451 2.516c0 .692.09 1.364.253 2H6.34A2.34 2.34 0 004 9.34v9.32C4 20.307 5.694 22 7.34 22h9.32c1.647 0 3.34-1.694 3.34-3.34V9.34a2.34 2.34 0 00-2.34-2.34h-2.221c.126-.628.187-1.293.187-1.973a6.935 6.935 0 00-.264-1.44z"
                            />
                        </svg>
                        <h2 className="text-sm font-medium text-white mb-1">No code available</h2>
                        <p className="text-xs text-gray-400">
                            Start by describing what your agent should do in the chat. The code will be generated here automatically.
                        </p>
                    </div>
                ) : (
                    <div className="bg-neutral-900 border border-neutral-800 rounded-md min-h-[400px] overflow-auto">
                        {codeLoading ? (
                            <div className="flex flex-col items-center justify-center h-full min-h-[400px] bg-neutral-900 border border-neutral-800 rounded-md text-center text-gray-400 p-6 animate-pulse">
                                <svg
                                    className="w-10 h-10 text-indigo-500 animate-spin mb-3"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                    />
                                    <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8v8z"
                                    />
                                </svg>
                                <p className="text-sm text-white">Generating code for you...</p>
                                <p className="text-xs text-gray-400 mt-1">Please wait while we build your agent logic.</p>
                            </div>
                        ) : (
                            <SyntaxHighlighter
                                language="python"
                                style={vscDarkPlus}
                                customStyle={{
                                    background: 'transparent',
                                    fontSize: '0.85rem',
                                    padding: '1rem',
                                    borderRadius: '0.5rem',
                                    margin: 0,
                                }}
                                wrapLines
                                showLineNumbers
                                lineProps={{ style: { whiteSpace: 'pre-wrap' } }}
                            >
                                {code}
                            </SyntaxHighlighter>
                        )}

                    </div>
                )}
            </div>

            {showSaveDialog && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
                    <div className="bg-neutral-900 border border-neutral-800 rounded-md p-6 w-full max-w-sm">
                        <h3 className="text-white text-sm font-semibold mb-2">Save Version</h3>
                        <textarea
                            value={saveComment}
                            onChange={(e) => setSaveComment(e.target.value)}
                            className="w-full h-20 text-sm bg-neutral-800 text-white rounded-md p-2 outline-none"
                            placeholder="Add a comment (optional)"
                        />
                        <div className="flex justify-end gap-2 mt-4">
                            <button
                                onClick={() => setShowSaveDialog(false)}
                                className="text-sm text-gray-400 hover:text-white"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSave}
                                className="text-sm bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1 rounded-md"
                            >
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ValidationPopup
                showModal={showValidationPopup}
                onClose={() => setShowValidationPopup(false)}
                outputData={validationData.outputData}
                timeTaken={validationData.timeTaken}
                llmUsed={validationData.llmUsed}
                errorDetails={validationData.errorDetails}
                entityInfo={validationData.entityInfo}
            />
        </div>
    );
};

export default AgentPlayground;
